import React, { useState, useEffect, useContext, useMemo } from "react";
import toast from "react-hot-toast";
import { Activity, CalendarPlus, Eye, Pencil, Plus, Search, Trash2, UserCheck, Users, X } from "lucide-react";
import { deleteUser, getAllUsers, createUser, updateUser } from "../../business/UserBusiness";
import { getSubscriptions } from "../../business/SalesBusiness";
import { PROVIDERS, SITUATIONS, planLabel, relativeDue, shortDate } from "../../constants/sales";
import { AuthContext } from "../../context/Auth";
import {
  Avatar, Badge, Button, Card, ConfirmModal, EmptyState, Field, IconButton, Modal, Page, PageHeader,
  Segmented, Skeleton, SortHeader, Stat, inputClass,
} from "../../components/ui";

// A conta do Alexandre só pode ser editada ou excluída por ele mesmo
const OWNER_EMAIL = 'alexandresaintss@gmail.com';

const ROLES = { user: ['Usuário', 'blue'], admin: ['Administrador', 'red'], patner: ['Parceiro', 'green'], trial: ['Trial', 'amber'] };
const roleLabel = (role) => ROLES[role]?.[0] || role;
const roleTone = (role) => ROLES[role]?.[1] || 'neutral';

const EMPTY_USER = { username: '', email: '', phone: '', password: '', role: 'user' };
const DAY = 24 * 60 * 60 * 1000;

// Filtro por assinatura: o status no Controlly e as situações cruzadas com o gateway
const SUBSCRIPTION_FILTERS = [
  { value: 'all', label: 'Todas', test: () => true },
  { value: 'active', label: 'Ativas', test: (u) => u?.subscription === 'active' },
  { value: 'inactive', label: 'Inativas', test: (u) => u?.subscription !== 'active' },
  { value: 'expired_active', label: SITUATIONS.expired_active.label, test: (u, s) => s?.situation === 'expired_active' },
  { value: 'active_no_payment', label: SITUATIONS.active_no_payment.label, test: (u, s) => s?.situation === 'active_no_payment' },
];

const formatDate = (value) => new Date(value).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
const formatLastUse = (value) => value
  ? new Date(value).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" })
  : 'Nunca';

export const Home = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [subscriptionFilter, setSubscriptionFilter] = useState("all");
  // Plano, gateway e vencimento de cada conta (id do usuário → linha de /admin/subscriptions)
  const [billing, setBilling] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [deletingPerson, setDeletingPerson] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState(EMPTY_USER);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  const adminName = localStorage.getItem("@Auth:name");
  const { user } = useContext(AuthContext);
  const isLocked = (person) => person?.email === OWNER_EMAIL && user?.email !== OWNER_EMAIL;

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const [response, subscriptions] = await Promise.all([
        getAllUsers(),
        // Sem os dados dos gateways a lista continua funcionando, só sem plano e vencimento
        getSubscriptions().catch(() => null),
      ]);
      setUsers(response?.data || []);
      setBilling(Object.fromEntries((subscriptions?.rows || []).filter((r) => r.user).map((r) => [r.user._id, r])));
    } catch (err) {
      toast.error('Erro ao carregar os usuários');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  // Números do topo, tirados da própria lista
  const stats = useMemo(() => {
    const now = Date.now();
    return {
      total: users.length,
      active: users.filter((u) => u?.subscription === 'active').length,
      newThisMonth: users.filter((u) => u?.createdAt && now - new Date(u.createdAt) < 30 * DAY).length,
      usedThisWeek: users.filter((u) => u?.lastUserRequest && now - new Date(u.lastUserRequest) < 7 * DAY).length,
    };
  }, [users]);

  const roleOptions = [
    { value: 'all', label: 'Todos', count: users.length },
    ...Object.entries(ROLES).map(([value, [label]]) => ({ value, label, count: users.filter((u) => u?.role === value).length })),
  ];

  const subscriptionOptions = SUBSCRIPTION_FILTERS.map(({ value, label, test }) => ({ value, label, count: users.filter((u) => test(u, billing[u._id])).length }));

  const sortedUsers = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const matchesSubscription = SUBSCRIPTION_FILTERS.find((f) => f.value === subscriptionFilter).test;
    const list = users.filter((u) =>
      (u?.username?.toLowerCase().includes(term) || u?.email?.toLowerCase().includes(term)) &&
      (roleFilter === 'all' || u?.role === roleFilter) &&
      matchesSubscription(u, billing[u._id])
    );
    const value = {
      username: (u) => u.username?.toLowerCase() || '',
      createdAt: (u) => new Date(u.createdAt || 0).getTime(),
      transactions: (u) => u.transactions?.length || 0,
      lastLogin: (u) => new Date(u.lastUserRequest || u.createdAt || 0).getTime(),
      accessUntil: (u) => new Date(billing[u._id]?.gateway?.accessUntil || 0).getTime(),
    }[sortBy];
    return [...list].sort((a, b) => {
      const [x, y] = sortOrder === 'asc' ? [value(a), value(b)] : [value(b), value(a)];
      return typeof x === 'string' ? x.localeCompare(y) : x - y;
    });
  }, [users, billing, searchTerm, roleFilter, subscriptionFilter, sortBy, sortOrder]);

  const handleSortChange = (column) => {
    if (sortBy === column) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortBy(column); setSortOrder('desc'); }
  };

  const openEdit = (person) => {
    setSelectedPerson(null);
    setEditingUser({
      _id: person._id,
      username: person.username,
      email: person.email || '',
      phone: person.phone || '',
      role: person.role || 'user',
      subscription: person.subscription || 'inactive',
    });
  };

  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.email || !newUser.phone || !newUser.password) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    try {
      setSaving(true);
      await createUser(newUser);
      toast.success('Usuário criado!');
      setShowCreateModal(false);
      setNewUser(EMPTY_USER);
      fetchUsers();
    } catch (error) {
      toast.error(error?.response?.data?.msg || 'Erro ao criar usuário');
    } finally {
      setSaving(false);
    }
  };

  const handleEditUser = async () => {
    if (!editingUser.email || !editingUser.phone) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    try {
      setSaving(true);
      const { email, phone, role, subscription } = editingUser;
      await updateUser(editingUser._id, { email, phone, role, subscription });
      toast.success('Usuário atualizado!');
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error?.response?.data?.msg || 'Erro ao atualizar usuário');
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteUser = async () => {
    try {
      setSaving(true);
      await deleteUser(deletingPerson._id);
      toast.success('Usuário removido');
      setDeletingPerson(null);
      setSelectedPerson(null);
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao excluir o usuário');
    } finally {
      setSaving(false);
    }
  };

  const setNew = (field, value) => setNewUser((prev) => ({ ...prev, [field]: value }));
  const setEdit = (field, value) => setEditingUser((prev) => ({ ...prev, [field]: value }));

  return (
    <Page>
      <PageHeader title="Usuários" description={`Olá, ${adminName || 'admin'}. Todas as contas do Controlly.`}>
        <Button icon={Plus} onClick={() => setShowCreateModal(true)}>Criar usuário</Button>
      </PageHeader>

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <Stat label="Total de usuários" value={stats.total} icon={Users} loading={isLoading} />
        <Stat label="Assinaturas ativas" value={stats.active} icon={UserCheck} tone="green" loading={isLoading}
          hint={stats.total ? `${Math.round((stats.active / stats.total) * 100)}% das contas` : null} />
        <Stat label="Novos em 30 dias" value={stats.newThisMonth} icon={CalendarPlus} tone="blue" loading={isLoading} />
        <Stat label="Usaram na semana" value={stats.usedThisWeek} icon={Activity} tone="amber" loading={isLoading} hint="Falaram com a Nath nos últimos 7 dias" />
      </div>

      <Card className="overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-neutral-100 p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between dark:border-white/[0.05]">
          <div className="relative w-full lg:max-w-sm">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-400" />
            <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Buscar por nome ou e-mail..." className={`${inputClass} pl-9 pr-8`} />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600" aria-label="Limpar busca"><X className="size-4" /></button>
            )}
          </div>
          <Segmented options={roleOptions} value={roleFilter} onChange={setRoleFilter} />
        </div>
        <div className="border-b border-neutral-100 px-4 py-3 sm:px-5 dark:border-white/[0.05]">
          <Segmented options={subscriptionOptions} value={subscriptionFilter} onChange={setSubscriptionFilter} />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-neutral-100 dark:border-white/[0.05]">
              <tr>
                <SortHeader label="Usuário" active={sortBy === 'username'} order={sortOrder} onClick={() => handleSortChange('username')} className="pl-5" />
                <th className="hidden px-4 py-3 text-left text-xs font-medium text-neutral-500 min-[1800px]:table-cell dark:text-neutral-400">Telefone</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400">Papel</th>
                <th className="hidden px-4 py-3 text-left text-xs font-medium text-neutral-500 md:table-cell dark:text-neutral-400">Assinatura</th>
                <SortHeader label="Vencimento" active={sortBy === 'accessUntil'} order={sortOrder} onClick={() => handleSortChange('accessUntil')} className="hidden lg:table-cell" />
                <SortHeader label="Registros" active={sortBy === 'transactions'} order={sortOrder} onClick={() => handleSortChange('transactions')} className="hidden sm:table-cell" />
                <SortHeader label="Criado em" active={sortBy === 'createdAt'} order={sortOrder} onClick={() => handleSortChange('createdAt')} className="hidden xl:table-cell" />
                <SortHeader label="Último uso" active={sortBy === 'lastLogin'} order={sortOrder} onClick={() => handleSortChange('lastLogin')} className="hidden xl:table-cell" />
                <th className="w-px px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-white/[0.05]">
              {isLoading ? (
                Array.from({ length: 6 }, (_, i) => (
                  <tr key={i}>
                    <td className="py-3 pl-5 pr-4"><div className="flex items-center gap-3"><Skeleton className="size-10 rounded-full" /><div className="flex flex-col gap-2"><Skeleton className="h-3.5 w-32" /><Skeleton className="h-3 w-44" /></div></div></td>
                    <td className="hidden px-4 min-[1800px]:table-cell"><Skeleton className="h-3.5 w-28" /></td>
                    <td className="px-4"><Skeleton className="h-5 w-20 rounded-full" /></td>
                    <td className="hidden px-4 md:table-cell"><Skeleton className="h-5 w-16 rounded-full" /></td>
                    <td className="hidden px-4 lg:table-cell"><Skeleton className="h-3.5 w-20" /></td>
                    <td className="hidden px-4 sm:table-cell"><Skeleton className="h-3.5 w-8" /></td>
                    <td className="hidden px-4 xl:table-cell"><Skeleton className="h-3.5 w-24" /></td>
                    <td className="hidden px-4 xl:table-cell"><Skeleton className="h-3.5 w-24" /></td>
                    <td />
                  </tr>
                ))
              ) : sortedUsers.length === 0 ? (
                <tr><td colSpan={9}><EmptyState icon={Users} title="Nenhum usuário encontrado" description={searchTerm ? 'Tente outro nome ou e-mail.' : null} /></td></tr>
              ) : (
                sortedUsers.map((person) => (
                  <tr key={person._id || person.email} onClick={() => setSelectedPerson(person)} className="cursor-pointer transition-colors hover:bg-neutral-50 dark:hover:bg-white/[0.02]">
                    <td className="py-3 pl-5 pr-4">
                      <div className="flex items-center gap-3">
                        <Avatar name={person.username} />
                        <div className="min-w-0">
                          <p className="max-w-[180px] truncate text-sm text-neutral-900 dark:text-neutral-100">{person.username}</p>
                          <p className="max-w-[220px] truncate text-xs text-neutral-500">{person.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="hidden whitespace-nowrap px-4 text-sm text-neutral-600 min-[1800px]:table-cell dark:text-neutral-300">{person.phone}</td>
                    <td className="whitespace-nowrap px-4"><Badge tone={roleTone(person.role)}>{roleLabel(person.role)}</Badge></td>
                    <td className="hidden whitespace-nowrap px-4 md:table-cell">
                      <Badge tone={person.subscription === 'active' ? 'green' : 'neutral'}>{person.subscription === 'active' ? 'Ativa' : 'Inativa'}</Badge>
                      {billing[person._id]?.gateway && (
                        <p className="mt-1 text-xs text-neutral-500">{planLabel(billing[person._id].gateway.plan)} · {PROVIDERS[billing[person._id].gateway.provider]?.label}</p>
                      )}
                    </td>
                    <td className="hidden whitespace-nowrap px-4 lg:table-cell">
                      {billing[person._id]?.gateway?.accessUntil ? <>
                        <p className="text-sm text-neutral-700 dark:text-neutral-300">{shortDate(billing[person._id].gateway.accessUntil)}</p>
                        <p className={`text-xs ${billing[person._id].situation === 'expired_active' ? 'text-red-500' : 'text-neutral-500'}`}>{relativeDue(billing[person._id].gateway.accessUntil)}</p>
                      </> : <span className="text-sm text-neutral-400">—</span>}
                    </td>
                    <td className="hidden px-4 text-sm text-neutral-600 sm:table-cell dark:text-neutral-300">{person.transactions?.length || 0}</td>
                    <td className="hidden whitespace-nowrap px-4 text-sm text-neutral-500 xl:table-cell">{person.createdAt ? formatDate(person.createdAt) : '—'}</td>
                    <td className="hidden whitespace-nowrap px-4 text-sm text-neutral-500 xl:table-cell">{formatLastUse(person.lastUserRequest)}</td>
                    <td className="px-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-0.5">
                        <IconButton icon={Eye} label="Ver detalhes" onClick={() => setSelectedPerson(person)} />
                        <IconButton icon={Pencil} label={isLocked(person) ? 'Só o próprio Alexandre pode editar' : 'Editar'} disabled={isLocked(person)} onClick={() => openEdit(person)} />
                        <IconButton icon={Trash2} tone="danger" label={isLocked(person) ? 'Só o próprio Alexandre pode excluir' : 'Excluir'} disabled={isLocked(person)} onClick={() => setDeletingPerson(person)} />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {!isLoading && sortedUsers.length > 0 && (
          <p className="border-t border-neutral-100 px-5 py-3 text-xs text-neutral-500 dark:border-white/[0.05]">{sortedUsers.length} de {users.length} usuários</p>
        )}
      </Card>

      {showCreateModal && (
        <Modal
          title="Criar usuário"
          onClose={() => setShowCreateModal(false)}
          footer={<>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={handleCreateUser} loading={saving}>Criar usuário</Button>
          </>}
        >
          <div className="flex flex-col gap-3">
            <Field label="Nome"><input className={inputClass} placeholder="João Silva" value={newUser.username} onChange={(e) => setNew('username', e.target.value)} /></Field>
            <Field label="E-mail"><input type="email" className={inputClass} placeholder="joao@email.com" value={newUser.email} onChange={(e) => setNew('email', e.target.value)} /></Field>
            <Field label="Telefone" hint="Com o código do país na frente (55...)"><input type="tel" className={inputClass} placeholder="5511999999999" value={newUser.phone} onChange={(e) => setNew('phone', e.target.value)} /></Field>
            <Field label="Senha"><input type="password" className={inputClass} placeholder="••••••••" value={newUser.password} onChange={(e) => setNew('password', e.target.value)} /></Field>
            <Field label="Papel">
              <select className={inputClass} value={newUser.role} onChange={(e) => setNew('role', e.target.value)}>
                {Object.entries(ROLES).map(([value, [label]]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </Field>
          </div>
        </Modal>
      )}

      {editingUser && (
        <Modal
          title="Editar usuário"
          onClose={() => setEditingUser(null)}
          footer={<>
            <Button variant="secondary" onClick={() => setEditingUser(null)} disabled={saving}>Cancelar</Button>
            <Button onClick={handleEditUser} loading={saving}>Salvar</Button>
          </>}
        >
          <div className="flex flex-col gap-3">
            <Field label="Nome" hint="O nome não pode ser alterado"><input className={inputClass} value={editingUser.username} disabled /></Field>
            <Field label="E-mail"><input type="email" className={inputClass} value={editingUser.email} onChange={(e) => setEdit('email', e.target.value)} /></Field>
            <Field label="Telefone" hint="Com o código do país na frente (55...)"><input type="tel" className={inputClass} value={editingUser.phone} onChange={(e) => setEdit('phone', e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Papel">
                <select className={inputClass} value={editingUser.role} onChange={(e) => setEdit('role', e.target.value)}>
                  {Object.entries(ROLES).map(([value, [label]]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </Field>
              <Field label="Assinatura">
                <select className={inputClass} value={editingUser.subscription} onChange={(e) => setEdit('subscription', e.target.value)}>
                  <option value="active">Ativa</option>
                  <option value="inactive">Inativa</option>
                </select>
              </Field>
            </div>
          </div>
        </Modal>
      )}

      {selectedPerson && (
        <Modal
          title="Detalhes do usuário"
          onClose={() => setSelectedPerson(null)}
          footer={!isLocked(selectedPerson) && <>
            <Button variant="secondary" icon={Trash2} className="text-red-500" onClick={() => setDeletingPerson(selectedPerson)}>Excluir</Button>
            <Button icon={Pencil} onClick={() => openEdit(selectedPerson)}>Editar</Button>
          </>}
        >
          <div className="flex items-center gap-3">
            <Avatar name={selectedPerson.username} size="size-14" />
            <div className="min-w-0">
              <p className="truncate text-base font-medium">{selectedPerson.username}</p>
              <div className="mt-1 flex gap-1.5">
                <Badge tone={roleTone(selectedPerson.role)}>{roleLabel(selectedPerson.role)}</Badge>
                <Badge tone={selectedPerson.subscription === 'active' ? 'green' : 'neutral'}>{selectedPerson.subscription === 'active' ? 'Assinatura ativa' : 'Assinatura inativa'}</Badge>
                {billing[selectedPerson._id] && ['expired_active', 'paid_inactive', 'expiring'].includes(billing[selectedPerson._id].situation) && (
                  <Badge tone={SITUATIONS[billing[selectedPerson._id].situation].tone}>{SITUATIONS[billing[selectedPerson._id].situation].label}</Badge>
                )}
              </div>
            </div>
          </div>
          <dl className="mt-5 divide-y divide-neutral-100 rounded-2xl bg-neutral-50 px-4 text-sm dark:divide-white/[0.05] dark:bg-white/[0.03]">
            {[
              ['E-mail', selectedPerson.email],
              ['Telefone', selectedPerson.phone],
              ['Plano', billing[selectedPerson._id]?.gateway ? `${planLabel(billing[selectedPerson._id].gateway.plan)} · ${PROVIDERS[billing[selectedPerson._id].gateway.provider]?.label}` : 'Sem assinatura nos gateways'],
              ['Vencimento', billing[selectedPerson._id]?.gateway?.accessUntil ? `${shortDate(billing[selectedPerson._id].gateway.accessUntil)} (${relativeDue(billing[selectedPerson._id].gateway.accessUntil)})` : '—'],
              ['Último pagamento', shortDate(billing[selectedPerson._id]?.gateway?.lastPaymentAt)],
              ['Registros', selectedPerson.transactions?.length || 0],
              ['Criado em', selectedPerson.createdAt ? formatDate(selectedPerson.createdAt) : '—'],
              ['Último uso', formatLastUse(selectedPerson.lastUserRequest)],
              ['ID', selectedPerson._id],
            ].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between gap-4 py-2.5">
                <dt className="text-neutral-500">{label}</dt>
                <dd className="min-w-0 break-all text-right text-neutral-900 dark:text-neutral-100">{value}</dd>
              </div>
            ))}
          </dl>
        </Modal>
      )}

      {deletingPerson && (
        <ConfirmModal title="Excluir usuário" confirmLabel="Excluir" loading={saving} onConfirm={confirmDeleteUser} onClose={() => setDeletingPerson(null)}>
          A conta de <b className="text-neutral-900 dark:text-neutral-100">{deletingPerson.username}</b> ({deletingPerson.email}) vai ser apagada. Isso não pode ser desfeito.
        </ConfirmModal>
      )}
    </Page>
  );
};
